import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { fetchGallery } from "@/lib/api";

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    fetchGallery()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [items]);

  const visible = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <div className="bg-[#F5DCD5] min-h-screen" data-testid="gallery-page">
      <Navigation />

      <section className="pt-32 md:pt-40 pb-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#B37C7C] hover:text-[#9A6565] mb-8"
            data-testid="gallery-back-home"
          >
            <ArrowLeft size={14} />
            Back
          </a>
          <div className="text-xs uppercase tracking-[0.22em] text-[#B37C7C] font-medium mb-5">
            The Full Gallery
          </div>
          <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-[#362C2C] leading-[0.98] mb-8 max-w-4xl">
            All our cookies,
            <span className="italic font-accent-serif text-[#B37C7C]"> in one place.</span>
          </h1>
          <p className="text-[#5C5252] leading-relaxed max-w-2xl text-base md:text-lg">
            Halloween, Thanksgiving, birthdays, showers, and custom orders — a growing
            collection from our Aubrey studio.
          </p>
        </div>
      </section>

      {categories.length > 2 && (
        <section className="px-6 md:px-12 lg:px-20 pb-6">
          <div className="max-w-7xl mx-auto flex flex-wrap gap-2 border-b border-[#EFE8E8] pb-6">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`text-sm px-4 py-2 rounded-full border transition-all ${
                  filter === c
                    ? "border-[#B37C7C] bg-[#B37C7C] text-white"
                    : "border-[#EFE8E8] bg-white text-[#5C5252] hover:border-[#B37C7C] hover:text-[#B37C7C]"
                }`}
                data-testid={`gallery-filter-${c}`}
              >
                {c}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="py-24 text-center text-[#5C5252]">Loading the portfolio…</div>
          ) : visible.length === 0 ? (
            <div className="py-24 text-center">
              <div className="font-serif-display text-2xl text-[#362C2C] mb-2">
                Nothing here yet.
              </div>
              <p className="text-sm text-[#5C5252]">
                New sets will appear here as they're added.
              </p>
            </div>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
              data-testid="gallery-page-grid"
            >
              {visible.map((item) => (
                <figure
                  key={item.id}
                  className="group relative overflow-hidden rounded-lg bg-[#F7EBEB] border border-[#EFE8E8]"
                  data-testid={`gallery-page-item-${item.id}`}
                >
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
                    />
                  </div>
                  <figcaption className="p-6">
                    {item.category && (
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#B37C7C] mb-2">
                        {item.category}
                      </div>
                    )}
                    <div className="font-serif-display text-2xl text-[#362C2C] mb-1">
                      {item.title}
                    </div>
                    {item.blurb && (
                      <div className="text-sm text-[#5C5252] leading-relaxed">
                        {item.blurb}
                      </div>
                    )}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}

          <div className="mt-20 text-center">
            <a
              href="/#order"
              className="inline-flex items-center gap-2 bg-[#B37C7C] text-white px-7 py-3.5 rounded-md hover:bg-[#9A6565] hover:-translate-y-0.5 hover:shadow-lg shadow-[#B37C7C]/20 transition-all text-sm tracking-wide"
              data-testid="gallery-page-cta"
            >
              Start your order
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
